// Pinia store for the External communication map: the derived message flows
// crossing the organisation boundary in three buckets — typed parties (the
// business tier), decision-mapped applications (the IT tier) and unresolved
// pools (both tiers) — plus the pool-triage decision records and the
// resolution verbs that write them.
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { Ref, computed, ref } from "vue";

import type { ExternalPartyCommunication } from "@/components/Applications/external-communication";
import {
    isAbortError,
    useLatestRequest,
} from "@/composables/use-latest-request";
import { api, type types } from "@/lib/openapi";
import { PartyType } from "@/lib/openapi/gen";

export type UntypedParty = types.UntypedParty;
export type ApplicationCommunication = types.ApplicationCommunication;
export type ExternalCommunicationMap = types.ExternalCommunicationMap;
export type ExternalParty = types.ExternalParty;
export type PoolTriageDecision = types.PoolTriageDecision;
export type PoolRolePromotion = types.PoolRolePromotion;
export type StekRole = types.StekRole;

/** Input for creating a typed ExternalParty (the "type this party" flow). */
export type CreatePartyInput = {
    name: string;
    partyType: PartyType;
    aliases?: string[];
    description?: string | null;
};

/**
 * Input for updating a typed ExternalParty. Omitted fields stay unchanged;
 * `aliases` replaces the whole list when provided; `clearDescription`
 * explicitly clears the description (a missing `description` alone means
 * "leave unchanged").
 */
export type UpdatePartyInput = {
    externalPartyId: string;
    name?: string;
    partyType?: PartyType;
    aliases?: string[];
    description?: string;
    clearDescription?: boolean;
};

export { PartyType };

export const useExternalCommunicationStore = defineStore(
    "externalCommunication",
    () => {
        const toast = useToast();

        const parties: Ref<ExternalPartyCommunication[]> = ref([]);
        // Pools decision-mapped to applications — the IT-tier bucket.
        const applications: Ref<ApplicationCommunication[]> = ref([]);
        const unresolved: Ref<UntypedParty[]> = ref([]);
        // Full typed parties (aliases, description) — the map only carries
        // lightweight refs, so the party page needs this list too.
        const typedParties: Ref<ExternalParty[]> = ref([]);
        // Pool-triage decision records (active + retracted history) — the
        // Triage page's Resolved rail and the undo path.
        const decisions: Ref<PoolTriageDecision[]> = ref([]);
        const isLoading: Ref<boolean> = ref(false);
        // Latest-request guard: each fetchMap gets its own AbortController +
        // token, so a superseded run can neither abort the current one nor
        // commit stale results.
        const { begin, isCurrent } = useLatestRequest();

        const fetchMap = async () => {
            const { token, signal } = begin();

            isLoading.value = true;
            try {
                const { data, response } =
                    await api.startDevApplicationMgtGetExternalCommunicationMapStartPost(
                        { signal }
                    );

                // A newer request superseded us while fetching — commit nothing.
                if (!isCurrent(token)) {
                    return;
                }

                if (response.ok && data) {
                    parties.value = data.parties ?? [];
                    applications.value = data.applications ?? [];
                    unresolved.value = data.unresolved ?? [];
                } else if (!response.ok) {
                    toast.add({
                        severity: "error",
                        summary: "Could not load external communication",
                        detail: "Server returned " + response.status,
                    });
                }
            } catch (e) {
                if (isAbortError(e)) {
                    return;
                }
                throw e;
            } finally {
                // Only the current request owns the loading flag.
                if (isCurrent(token)) {
                    isLoading.value = false;
                }
            }
        };

        const fetchParties = async () => {
            const { data, response } =
                await api.startDevExternalPartyMgtGetExternalPartiesStartPost(
                    {}
                );
            if (response.ok && data) {
                typedParties.value = data;
            } else {
                toast.add({
                    severity: "error",
                    summary: "Could not load external parties",
                    detail: "Server returned " + response.status,
                });
            }
        };

        /** The full typed party, once `fetchParties` has run. */
        const getParty = (partyId: string): ExternalParty | undefined =>
            typedParties.value.find((party) => party.identity === partyId);

        /** The party's derived flows, once `fetchMap` has run. */
        const communicationForParty = (
            partyId: string
        ): ExternalPartyCommunication | undefined =>
            parties.value.find((item) => item.party.id === partyId);

        const createParty = async (
            input: CreatePartyInput
        ): Promise<ExternalParty | undefined> => {
            isLoading.value = true;
            try {
                const { data, response } =
                    await api.startDevExternalPartyMgtCreateExternalPartyStartPost(
                        {
                            body: {
                                name: input.name,
                                party_type: input.partyType,
                                aliases: input.aliases?.length
                                    ? input.aliases
                                    : null,
                                description: input.description?.trim() || null,
                            },
                        }
                    );
                if (response.ok && data) {
                    // Refetch: the freshly typed party absorbs its pool's
                    // flows out of the untyped list.
                    await fetchMap();
                    return data;
                } else {
                    toast.add({
                        severity: "error",
                        summary: "Could not create external party",
                        detail: "Server returned " + response.status,
                    });
                }
            } finally {
                isLoading.value = false;
            }
        };

        /**
         * Update a typed party (name, type, aliases, description). On
         * success the typed list is patched with the returned party and the
         * derived map is refetched — name/alias/type edits change how pool
         * names resolve onto parties.
         */
        const updateParty = async (
            input: UpdatePartyInput
        ): Promise<ExternalParty | undefined> => {
            const { data, response } =
                await api.startDevExternalPartyMgtUpdateExternalPartyStartPost({
                    body: {
                        external_party_id: input.externalPartyId,
                        name: input.name ?? null,
                        party_type: input.partyType ?? null,
                        aliases: input.aliases ?? null,
                        description: input.description ?? null,
                        clear_description: input.clearDescription ?? false,
                    },
                });
            if (response.ok && data) {
                typedParties.value = typedParties.value.map((existing) =>
                    existing.identity === data.identity ? data : existing
                );
                await fetchMap();
                return data;
            }
            toast.add({
                severity: "error",
                summary: "Could not update external party",
                detail: "Server returned " + response.status,
            });
        };

        /**
         * Delete a typed party. Its flows fall back to the untyped list, so
         * the map is refetched on success.
         */
        const deleteParty = async (partyId: string): Promise<boolean> => {
            const { response } =
                await api.startDevExternalPartyMgtDeleteExternalPartyStartPost({
                    body: { external_party_id: partyId },
                });
            if (response.ok) {
                typedParties.value = typedParties.value.filter(
                    (party) => party.identity !== partyId
                );
                await fetchMap();
                return true;
            }
            toast.add({
                severity: "error",
                summary: "Could not delete external party",
                detail: "Server returned " + response.status,
            });
            return false;
        };

        const fetchDecisions = async () => {
            try {
                const { data, response } =
                    await api.startDevApplicationMgtGetPoolTriageDecisionsStartPost(
                        {}
                    );
                if (response.ok && data) {
                    decisions.value = data;
                }
            } catch {
                // The decision list decorates the triage rail — best-effort.
            }
        };

        // The stek-wide role list — candidates for the triage's "the pool is
        // another name for an existing role" picker.
        const stekRoles: Ref<StekRole[]> = ref([]);

        const fetchStekRoles = async () => {
            try {
                const { data, response } =
                    await api.startDevApplicationMgtGetStekRolesStartPost({});
                if (response.ok && data) {
                    stekRoles.value = data;
                }
            } catch {
                // The link picker degrades to create-only — best-effort.
            }
        };

        /** The single active decision per pool, newest wins. */
        const activeDecisions = computed(() =>
            decisions.value.filter((record) => record.status === "active")
        );

        /** Map + decisions refetch after any resolution verb. */
        const refreshAfterResolution = async () => {
            await Promise.all([fetchMap(), fetchDecisions()]);
        };

        const resolutionError = (summary: string, status: number) =>
            toast.add({
                severity: "error",
                summary,
                detail: "Server returned " + status,
                life: 5000,
            });

        /** Resolve a pool as a party: `partyId` links an existing one (the
         *  pool name becomes an alias), else a new party is created from
         *  `name`/`partyType`/`description` (defaulted from the pool). */
        const resolvePoolAsParty = async (input: {
            poolName: string;
            partyId?: string;
            name?: string;
            partyType?: PartyType;
            description?: string | null;
        }): Promise<PoolTriageDecision | undefined> => {
            const { data, response } =
                await api.startDevApplicationMgtResolvePoolAsPartyStartPost({
                    body: {
                        pool_name: input.poolName,
                        party_id: input.partyId ?? null,
                        name: input.name ?? null,
                        party_type: input.partyType ?? null,
                        description: input.description?.trim() || null,
                    },
                });
            if (response.ok && data) {
                await refreshAfterResolution();
                return data;
            }
            resolutionError("Could not resolve the pool", response.status);
        };

        /** Resolve a pool as an application — the explicit pool→application
         *  mapping. `absorbMatchingRoles` also claims same-named roles as
         *  the application's actors (the role→application conversion).
         *  `convertToLane` is the one-click accept of the internal-scope
         *  stack: the pool is remodeled as a lane in the same transaction —
         *  requires the previewed `proposalFingerprint`. */
        const resolvePoolAsApplication = async (input: {
            poolName: string;
            applicationId: string;
            absorbMatchingRoles?: boolean;
            appScope?: "internal" | "external" | null;
            proposalFingerprint?: string | null;
            convertToLane?: boolean;
        }): Promise<PoolTriageDecision | undefined> => {
            const { data, response } =
                await api.startDevApplicationMgtResolvePoolAsApplicationStartPost(
                    {
                        body: {
                            pool_name: input.poolName,
                            application_id: input.applicationId,
                            absorb_matching_roles:
                                input.absorbMatchingRoles ?? false,
                            app_scope: input.appScope ?? null,
                            proposal_fingerprint:
                                input.proposalFingerprint ?? null,
                            convert_to_lane: input.convertToLane ?? false,
                        },
                    }
                );
            if (response.ok && data) {
                await refreshAfterResolution();
                return data;
            }
            resolutionError("Could not resolve the pool", response.status);
        };

        /** Backfill the internal/external scope on an active application
         *  decision — an annotation on the record, not a new decision. */
        const setPoolAppScope = async (input: {
            poolName: string;
            appScope: "internal" | "external";
        }): Promise<PoolTriageDecision | undefined> => {
            const { data, response } =
                await api.startDevApplicationMgtSetPoolAppScopeStartPost({
                    body: {
                        pool_name: input.poolName,
                        app_scope: input.appScope,
                    },
                });
            if (response.ok && data) {
                await fetchDecisions();
                return data;
            }
            resolutionError("Could not set the scope", response.status);
        };

        /** Resolve a pool as an internal reference (organisation / domain /
         *  product / process). Record-only; the modeler's pool-reference
         *  selector remains the in-diagram binding. */
        const resolvePoolAsReference = async (input: {
            poolName: string;
            entityKind: "organisation" | "domain" | "product" | "process";
            entityId: string;
        }): Promise<PoolTriageDecision | undefined> => {
            const { data, response } =
                await api.startDevApplicationMgtResolvePoolAsReferenceStartPost(
                    {
                        body: {
                            pool_name: input.poolName,
                            entity_kind: input.entityKind,
                            entity_id: input.entityId,
                        },
                    }
                );
            if (response.ok && data) {
                await refreshAfterResolution();
                return data;
            }
            resolutionError("Could not resolve the pool", response.status);
        };

        /** Promote a pool to internal Roles (one per messaging domain,
         *  associated as one identity) — the internal-function answer.
         *  `roleId` links an existing role instead: it anchors the
         *  association and names the roles created in the other domains.
         *  `renameFrom` unifies sibling spellings: they are renamed to
         *  `poolName` inside their BPMN definitions and the merged pool
         *  resolves as one decision (the rename is never undone).
         *  `convertToLane` is the one-click accept of the whole stack: the
         *  pool is also remodeled as a lane, atomically — requires the
         *  previewed `proposalFingerprint`. */
        const resolvePoolAsRole = async (
            poolName: string,
            roleId?: string,
            renameFrom?: string[],
            proposalFingerprint?: string | null,
            convertToLane?: boolean
        ): Promise<PoolRolePromotion[] | undefined> => {
            const { data, response } =
                await api.startDevApplicationMgtPromotePoolToRoleStartPost({
                    body: {
                        pool_name: poolName,
                        role_id: roleId ?? null,
                        rename_from: renameFrom ?? null,
                        proposal_fingerprint: proposalFingerprint ?? null,
                        convert_to_lane: convertToLane ?? false,
                    },
                });
            if (response.ok && data) {
                await refreshAfterResolution();
                return data;
            }
            resolutionError("Could not promote the pool", response.status);
        };

        /** Undo: retract the pool's active decision (side effects reverted
         *  where honest); the pool returns to the unresolved queue. */
        const unresolvePool = async (
            poolName: string
        ): Promise<PoolTriageDecision | undefined> => {
            const { data, response } =
                await api.startDevApplicationMgtUnresolvePoolStartPost({
                    body: { pool_name: poolName },
                });
            if (response.ok && data) {
                await refreshAfterResolution();
                return data;
            }
            resolutionError("Could not undo the decision", response.status);
        };

        return {
            parties,
            applications,
            unresolved,
            typedParties,
            decisions,
            activeDecisions,
            stekRoles,
            isLoading,
            fetchMap,
            fetchParties,
            fetchDecisions,
            fetchStekRoles,
            getParty,
            communicationForParty,
            createParty,
            updateParty,
            deleteParty,
            resolvePoolAsParty,
            resolvePoolAsApplication,
            setPoolAppScope,
            resolvePoolAsReference,
            resolvePoolAsRole,
            unresolvePool,
        };
    }
);

export const useExternalCommunicationManager = () =>
    storeToRefs(useExternalCommunicationStore());
